import React from "react";
import { Card, CardBody, CardHeader, Divider, Select, SelectItem } from "@nextui-org/react";
import TableVehicle from "./review/TableVehicle";
import TableMaterial from "./review/TableMaterial";
import InlineEditInput from "../../../elements/InlineEditInput";
import { updateCompany, getFetcher, patchFetcherWithoutId } from "../../../../services/api";
import useSWR from "swr";
import toast from "react-hot-toast";

export default function Information({ data, isLoading, mutate }) {
  const companyId = data?.company?.id;
  const identitasPemohonId = data?.identitasPemohon?.id;

  // Fetch data KBLI
  const { data: kbli } = useSWR("/api/data-master/kbli", getFetcher);

  // Function untuk handle update berbagai field perusahaan
  function onSubmitCompanyField(fieldName, displayName) {
    return async (editData) => {
      const payload = { [fieldName]: editData.text };

      // Jika field adalah bidang usaha, tambahkan kode KBLI
      if (fieldName === "bidangUsaha") {
        const selectedKbli = kbli?.find((item) => item.judul === editData.text);
        payload.kodeKBLIBidangUsaha = selectedKbli ? selectedKbli.kode : null;
      }

      try {
        await updateCompany(companyId, payload);
        mutate();
        toast.success(`${displayName} berhasil diperbarui`);
      } catch (error) {
        console.error("Error updating company field:", error);
        toast.error(`Gagal mengupdate ${displayName}`);
      }
    };
  }

  // Function untuk handle update identitas pemohon
  function onSubmitIdentitasField(fieldName, displayName) {
    return async (editData) => {
      const payload = {
        id: identitasPemohonId,
        [fieldName]: editData.text,
      };

      try {
        await patchFetcherWithoutId("/api/rekom/permohonan/identitas-pemohon", payload);
        mutate();
        toast.success(`${displayName} berhasil diperbarui`);
      } catch (error) {
        console.error("Error updating identitas pemohon field:", error);
        const msg = error?.response?.data?.message || `Gagal mengupdate ${displayName}`;
        toast.error(msg);
      }
    };
  }

  const BidangUsahaSelect = ({ value, onSubmit }) => {
    const handleSelectionChange = (keys) => {
      const selectedValue = Array.from(keys)[0];
      if (selectedValue && selectedValue !== value) {
        onSubmit({ text: selectedValue });
      }
    };

    return (
      <Select variant="flat" size="sm" selectedKeys={value && value !== "-" ? [value] : []} onSelectionChange={handleSelectionChange} placeholder="Pilih bidang usaha..." className="min-w-[200px]" isLoading={!kbli}>
        {kbli?.map((item) => (
          <SelectItem key={item.judul} value={item.judul}>
            {`${item.kode} - ${item.judul}`}
          </SelectItem>
        ))}
      </Select>
    );
  };

  const identity = (item) => [
    {
      label: "Nama",
      value: <InlineEditInput value={item?.identitasPemohon?.namaPemohon ?? "-"} onSubmit={onSubmitIdentitasField("namaPemohon", "Nama Pemohon")} />,
    },
    {
      label: "Jabatan",
      value: <InlineEditInput value={item?.identitasPemohon?.jabatan ?? "-"} onSubmit={onSubmitIdentitasField("jabatan", "Jabatan")} />,
    },
    {
      label: "Email",
      value: <InlineEditInput value={item?.identitasPemohon?.email ?? "-"} onSubmit={onSubmitIdentitasField("email", "Email")} />,
    },
    {
      label: "No. Telepon",
      value: <InlineEditInput value={item?.identitasPemohon?.teleponFax ?? "-"} onSubmit={onSubmitIdentitasField("teleponFax", "No. Telepon")} />,
    },
    {
      label: "Alamat Domisili",
      value: <InlineEditInput multiline value={item?.identitasPemohon?.alamatDomisili ?? "-"} onSubmit={onSubmitIdentitasField("alamatDomisili", "Alamat Domisili")} />,
    },
    {
      label: "NPWP",
      value: <InlineEditInput value={item?.identitasPemohon?.npwp ?? "-"} onSubmit={onSubmitIdentitasField("npwp", "NPWP")} />,
    },
    {
      label: "KTP",
      value: <InlineEditInput value={item?.identitasPemohon?.ktp ?? "-"} onSubmit={onSubmitIdentitasField("ktp", "KTP")} />,
    },
  ];

  const company = (item) => [
    {
      label: "Nama Perusahaan",
      value: <InlineEditInput value={item?.company?.name ?? "-"} onSubmit={onSubmitCompanyField("name", "Nama Perusahaan")} />,
    },
    {
      label: "Email",
      value: <InlineEditInput value={item?.company?.emailKantor ?? "-"} onSubmit={onSubmitCompanyField("emailKantor", "Email")} />,
    },
    {
      label: "Telepon Kantor",
      value: <InlineEditInput value={item?.company?.telpKantor ?? "-"} onSubmit={onSubmitCompanyField("telpKantor", "Telepon Kantor")} />,
    },
    {
      label: "Fax Kantor",
      value: <InlineEditInput value={item?.company?.faxKantor ?? "-"} onSubmit={onSubmitCompanyField("faxKantor", "Fax Kantor")} />,
    },
    {
      label: "Bidang Usaha",
      value: <BidangUsahaSelect value={item?.company?.bidangUsaha ?? "-"} onSubmit={onSubmitCompanyField("bidangUsaha", "Bidang Usaha")} />,
    },
    {
      label: "Alamat Kantor",
      value: <InlineEditInput multiline value={item?.company?.alamatKantor ?? "-"} onSubmit={onSubmitCompanyField("alamatKantor", "Alamat Kantor")} />,
    },
    {
      label: "NIB",
      value: <InlineEditInput value={item?.company?.nib || item?.company?.nomorInduk} onSubmit={onSubmitCompanyField("nib", "NIB")} />,
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Card radius="sm">
        <CardHeader>Identitas Pemohon</CardHeader>
        <Divider />
        <CardBody className="flex flex-col gap-3">
          {identity(data).map((item, index) => (
            <div key={index} className={`flex flex-col ${item.column}`}>
              <span className="text-xs text-ink-400 uppercase">{item.label}</span>
              <div className="text-sm font-medium">{item.value}</div>
            </div>
          ))}
        </CardBody>
      </Card>
      <Card radius="sm">
        <CardHeader>Identitas Perusahaan</CardHeader>
        <Divider />
        <CardBody className="flex flex-col gap-3">
          {company(data).map((item, index) => (
            <div key={index} className={`flex flex-col ${item.column}`}>
              <span className="text-xs text-ink-400 uppercase">{item.label}</span>
              <div className="text-sm font-medium">{item.value}</div>
            </div>
          ))}
        </CardBody>
      </Card>
      <div className="col-span-2">
        <TableVehicle data={data} isLoading={isLoading} mutate={mutate} />
      </div>
      <div className="col-span-2">
        <TableMaterial data={data} isLoading={isLoading} mutate={mutate} />
      </div>
    </div>
  );
}
